<script setup lang="ts">
import { computed, ref, watch } from "vue";

type PoolItem = {
  items_id: number;
  code: string;
  name: string;
  sales_uom: string;
  sales_uom_qty: number;
  on_hand: number;
};

type FreePool = {
  promo_uid: string;
  promo_name: string;
  free_total_qty: number;
  free_uom_id: number | null;
  items: PoolItem[];
  expense_account_id: number | null;
};

type ChosenLine = { promo_uid: string; items_id: number; qty: number };

const props = defineProps<{
  pool: FreePool;
  // Existing override the cashier picked previously for this same promo
  // (e.g. modal re-opened to tweak the choice) — used to pre-fill qty
  // inputs instead of always resetting to the auto-pick.
  preselected?: ChosenLine[];
}>();

const emit = defineEmits<{
  (e: "apply", lines: ChosenLine[]): void;
  (e: "close"): void;
}>();

// Per-item cap in display UoM: how many of THIS item the cashier can
// actually give based on warehouse stock. Sales-UoM-qty translates the
// base inventory `on_hand` into the modal's display unit (typically CTN).
// We floor to whole units — partial cartons aren't a thing for the
// wholesale workflow this POS supports. Always intersect with the
// promo's free_total_qty so a high-stock item still can't exceed the
// promo's earned allotment.
const maxQtyFor = (item: PoolItem): number => {
  const packing = item.sales_uom_qty || 1;
  const inUom = Math.floor((Number(item.on_hand) || 0) / packing);
  return Math.max(0, Math.min(props.pool.free_total_qty, inUom));
};

// One qty input per pool item, keyed by items_id. Starts at 0 unless this
// item was preselected — then we honour the cashier's prior pick.
const qtyByItem = ref<Record<number, number>>({});

watch(
  () => props.pool,
  (pool) => {
    qtyByItem.value = {};
    if (!pool) return;
    for (const it of pool.items) qtyByItem.value[it.items_id] = 0;
    // Seed with prior selection if any; else distribute greedily across
    // items respecting each item's stock cap so we don't pre-fill an
    // out-of-stock item. The cashier can redistribute before saving.
    const seeded = (props.preselected ?? []).filter(
      (p) => p.promo_uid === pool.promo_uid
    );
    if (seeded.length > 0) {
      for (const s of seeded) {
        if (qtyByItem.value[s.items_id] !== undefined) {
          qtyByItem.value[s.items_id] = s.qty;
        }
      }
    } else {
      let remaining = pool.free_total_qty;
      for (const it of pool.items) {
        if (remaining <= 0) break;
        const cap = maxQtyFor(it);
        const take = Math.min(remaining, cap);
        if (take > 0) {
          qtyByItem.value[it.items_id] = take;
          remaining -= take;
        }
      }
    }
  },
  { immediate: true }
);

const totalSelected = computed(() =>
  Object.values(qtyByItem.value).reduce((s, q) => s + (Number(q) || 0), 0)
);

// Maximum the cashier can physically distribute given current stock —
// each item's per-row cap summed, then clamped to the promo's earned
// allotment. If every item is well-stocked this equals free_total_qty;
// if stock is short on every item it can be less. Drives the new
// "must give all the customer earned" validation rule below.
const maxAvailable = computed(() => {
  const totalStockableCap = props.pool.items.reduce(
    (s, it) => s + maxQtyFor(it),
    0
  );
  return Math.min(props.pool.free_total_qty, totalStockableCap);
});

// "Must give the customer everything they earned" semantics. The cashier
// can no longer save with a partial selection — the freebie belongs to the
// buyer and skipping it (or shorting them) requires explicit cancel of the
// whole sale. The Save button stays disabled until `totalSelected` exactly
// matches `maxAvailable`. Empty pool / all-out-of-stock degenerates to
// maxAvailable=0, so the cashier CAN save with zero in that genuine
// edge case (the freebie is simply unfulfillable).
const valid = computed(
  () => Math.abs(totalSelected.value - maxAvailable.value) < 0.0001
);

const overCap = computed(
  () => totalSelected.value > props.pool.free_total_qty + 0.0001
);

// True when the cashier has picked fewer than the customer is entitled to.
// Surfaces a clear "you must distribute N more units" hint so the
// disabled-save state isn't a mystery.
const shortfall = computed(
  () => Math.max(0, maxAvailable.value - totalSelected.value)
);

// Whether any pool item has positive stock — if not, the modal can't be
// confirmed at all and we show a clear "cancel to skip" message instead
// of the default "pick all earned" hint that would mislead the cashier.
const anyInStock = computed(() =>
  props.pool.items.some((it) => maxQtyFor(it) > 0)
);

// Snap the active row down to whichever cap binds first: this item's
// stock-derived max, or the remaining allotment after accounting for
// what's already on the other rows.
const onQtyInput = (itemsId: number) => {
  const item = props.pool.items.find((i) => i.items_id === itemsId);
  if (!item) return;
  const itemCap = maxQtyFor(item);
  const total = props.pool.free_total_qty;
  const others = Object.entries(qtyByItem.value)
    .filter(([k]) => Number(k) !== itemsId)
    .reduce((s, [, q]) => s + (Number(q) || 0), 0);
  const remainingCap = Math.max(0, total - others);
  const cap = Math.min(itemCap, remainingCap);
  if (qtyByItem.value[itemsId] > cap) qtyByItem.value[itemsId] = cap;
  if (qtyByItem.value[itemsId] < 0) qtyByItem.value[itemsId] = 0;
};

const handleSave = () => {
  if (!valid.value) return;
  const lines: ChosenLine[] = Object.entries(qtyByItem.value)
    .filter(([, q]) => Number(q) > 0)
    .map(([items_id, q]) => ({
      promo_uid: props.pool.promo_uid,
      items_id: Number(items_id),
      qty: Number(q),
    }));
  emit("apply", lines);
};
</script>

<template>
  <!-- Backdrop is an intentional click-trap, NOT a close affordance. The
       freebie is the customer's earned promo outcome, so dismissing the
       chooser by accidental outside-click (or ESC) would either short the
       buyer or silently skip the promo. The cashier MUST either confirm a
       full distribution (Save) or explicitly cancel the entire sale via the
       Cancel button — both `@click.self.stop.prevent` on the backdrop and
       `@keydown.esc.stop.prevent` on the focus-trap shell are no-ops that
       swallow the would-be close events. -->
  <div
    class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="free-item-chooser-title"
    tabindex="-1"
    @click.self.stop.prevent
    @keydown.esc.stop.prevent
  >
    <div
      class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full"
      @click.stop
    >
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <h3
          id="free-item-chooser-title"
          class="text-xl font-semibold text-gray-900 dark:text-white"
        >
          Choose Free Items
        </h3>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {{ pool.promo_name }} — distribute the customer's earned
          <span class="font-semibold">{{ pool.free_total_qty }}</span>
          free unit(s) across these items. You must allocate
          <span class="font-semibold">{{ maxAvailable }}</span>
          unit(s) before confirming.
        </p>
      </div>

      <div class="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
        <div
          v-for="it in pool.items"
          :key="it.items_id"
          class="grid grid-cols-12 items-center gap-3 border-b border-gray-100 dark:border-gray-700 pb-3 last:border-b-0"
          :class="maxQtyFor(it) <= 0 ? 'opacity-60' : ''"
        >
          <div class="col-span-7">
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ it.name }}
            </div>
            <div class="text-xs text-gray-500 dark:text-gray-400">
              {{ it.code }} · {{ it.sales_uom || "—" }} · stock
              <span
                :class="
                  maxQtyFor(it) > 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-red-600 dark:text-red-400'
                "
                >{{ it.on_hand }}</span
              >
              <span
                v-if="maxQtyFor(it) <= 0"
                class="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
              >
                Out of stock
              </span>
              <span
                v-else
                class="ml-2 text-[11px] text-gray-400 dark:text-gray-500"
              >
                max {{ maxQtyFor(it) }} {{ it.sales_uom || "" }}
              </span>
            </div>
          </div>
          <div class="col-span-5">
            <input
              type="number"
              :min="0"
              :max="maxQtyFor(it)"
              :readonly="maxQtyFor(it) <= 0"
              step="0.01"
              v-model.number="qtyByItem[it.items_id]"
              @input="onQtyInput(it.items_id)"
              class="w-full px-3 py-2 text-right border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
              :class="
                maxQtyFor(it) <= 0
                  ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                  : ''
              "
            />
          </div>
        </div>
      </div>

      <div class="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 text-sm">
        <div class="flex justify-between">
          <span class="text-gray-600 dark:text-gray-400">Selected</span>
          <span
            class="font-semibold"
            :class="
              overCap
                ? 'text-red-600 dark:text-red-400'
                : valid
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-amber-600 dark:text-amber-400'
            "
            >{{ totalSelected }} / {{ maxAvailable }} required</span
          >
        </div>
        <div
          v-if="overCap"
          class="mt-1 text-xs text-red-600 dark:text-red-400"
        >
          Exceeds the eligible quantity. Reduce by
          {{ (totalSelected - pool.free_total_qty).toFixed(2) }}.
        </div>
        <div
          v-else-if="shortfall > 0 && anyInStock"
          class="mt-1 text-xs text-amber-600 dark:text-amber-400"
        >
          You must allocate {{ shortfall }} more unit(s) before confirming —
          the customer is entitled to the full freebie.
        </div>
        <div
          v-else-if="!anyInStock"
          class="mt-1 text-xs text-red-600 dark:text-red-400"
        >
          No eligible items are in stock — cancel to abort the sale.
        </div>
        <div
          v-else-if="valid && maxAvailable < pool.free_total_qty"
          class="mt-1 text-xs text-gray-500 dark:text-gray-400"
        >
          Stock only covers {{ maxAvailable }} of the
          {{ pool.free_total_qty }} earned units — that's the most you can
          distribute right now.
        </div>
      </div>

      <div
        class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg flex justify-end gap-3"
      >
        <!-- "Cancel" aborts the WHOLE sale (the parent flow resolves to
             null and the caller MUST abort, per useSalesPromoFlow). The
             label spells that out so a cashier doesn't dismiss the chooser
             thinking it'll just close this modal and skip the freebie. -->
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Aborts this entire sale — the chooser cannot be skipped without cancelling."
        >
          Cancel Sale
        </button>
        <button
          @click="handleSave"
          :disabled="!valid"
          class="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Confirm Free Items
        </button>
      </div>
    </div>
  </div>
</template>
